import { useCallback, useEffect, useMemo, useState } from 'react'
import type { LibraryKey, Tag, TagGroup, TagGroupDef } from '@shared/types'
import { LIBRARIES, libraryDef } from '@shared/data/libraries'
import { useRemembered } from '../useRemembered'
import {
  createTag,
  createTagGroup,
  deleteTag,
  deleteTagGroup,
  getAssetRepository,
  mergeTags,
  renameTag,
  renameTagGroup,
  setTagGroup,
  swapTagGroupPositions,
} from '@shared/services'
import '../tags.css'

function countLabel(count: number): string {
  return `${count} image${count === 1 ? '' : 's'}`
}

export function TagManagerPage() {
  const repository = useMemo(() => getAssetRepository(), [])

  /**
   * Which library's tags. One Tag Manager with a switch, rather than one per
   * library (Huanrui, 2026-09-29): the work is the same in all three, only
   * the tags differ. Remembered, so it opens where it was left.
   */
  const [library, setLibrary] = useRemembered<LibraryKey>('ww.tags.library', 'audit')
  const def = libraryDef(library)

  const [tags, setTags] = useState<Tag[] | null>(null)
  const [groups, setGroups] = useState<TagGroupDef[]>([])
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  /** The id of whatever row is mid-write, so only that row locks. */
  const [busyId, setBusyId] = useState<string | null>(null)
  const [filter, setFilter] = useState('')

  const [editingId, setEditingId] = useState<string | null>(null)
  const [draftName, setDraftName] = useState('')

  const [mergingId, setMergingId] = useState<string | null>(null)
  const [mergeTargetId, setMergeTargetId] = useState('')

  const [newName, setNewName] = useState('')
  const [newGroup, setNewGroup] = useState<TagGroup>(def.defaultGroup)

  const [newCategory, setNewCategory] = useState('')
  const [editingGroup, setEditingGroup] = useState<string | null>(null)
  const [draftGroupLabel, setDraftGroupLabel] = useState('')
  const [showCategories, setShowCategories] = useState(false)

  const load = useCallback(async () => {
    try {
      const [tagList, groupList] = await Promise.all([
        repository.listTags(library),
        repository.listTagGroups(library),
      ])
      setTags(tagList)
      setGroups(groupList)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the tags.')
    }
  }, [repository, library])

  // A different library: its own tags, and nothing half-done carried over.
  useEffect(() => {
    setTags(null)
    setGroups([])
    setError(null)
    setNote(null)
    setEditingId(null)
    setMergingId(null)
    setEditingGroup(null)
    setNewGroup(libraryDef(library).defaultGroup)
  }, [library])

  useEffect(() => {
    void load()
  }, [load])

  const labelOf = useCallback(
    (slug: string) => groups.find((group) => group.slug === slug)?.label ?? slug,
    [groups],
  )

  /**
   * Every edit reloads the whole list rather than patching the row in place.
   * The counts move in ways the browser cannot work out on its own — a merge
   * changes two rows at once, and only the database knows how many pictures
   * already carried both tags — so guessing would leave numbers that look
   * authoritative and are wrong.
   */
  async function run(id: string, work: () => Promise<string>) {
    setBusyId(id)
    setError(null)
    setNote(null)
    try {
      const message = await work()
      await load()
      setNote(message)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not work.')
    } finally {
      setBusyId(null)
    }
  }

  function startRename(tag: Tag) {
    setEditingId(tag.id)
    setDraftName(tag.name)
    setMergingId(null)
  }

  function commitRename(tag: Tag) {
    const name = draftName.trim()
    setEditingId(null)
    if (!name || name === tag.name) return
    void run(tag.id, async () => {
      await renameTag(tag.id, name)
      return `Renamed to ${name}.`
    })
  }

  function startMerge(tag: Tag) {
    setMergingId(tag.id)
    setMergeTargetId('')
    setEditingId(null)
  }

  function commitMerge(source: Tag, target: Tag) {
    setMergingId(null)
    void run(source.id, async () => {
      const moved = await mergeTags(source.id, target.id)
      return `${countLabel(moved)} moved from ${source.name} to ${target.name}. ${source.name} is gone.`
    })
  }

  function commitGroupRename(group: TagGroupDef) {
    const label = draftGroupLabel.trim()
    setEditingGroup(null)
    if (!label || label === group.label) return
    void run(group.slug, async () => {
      await renameTagGroup(group.slug, label)
      return `Category renamed to ${label}.`
    })
  }

  /** How many tags sit in each category — the number that decides deletability. */
  const tagsPerGroup = useMemo(() => {
    const counts = new Map<string, number>()
    for (const tag of tags ?? []) counts.set(tag.group, (counts.get(tag.group) ?? 0) + 1)
    return counts
  }, [tags])

  const visible = useMemo(() => {
    if (!tags) return []
    const needle = filter.trim().toLowerCase()
    return needle ? tags.filter((tag) => tag.name.toLowerCase().includes(needle)) : tags
  }, [tags, filter])

  /**
   * One section per category, in the filter's own order, then anything filed
   * under a category the table does not know about. Empty categories are still
   * drawn: a row saying "nothing here yet" is where you go to put something,
   * while a missing row says the category does not exist.
   */
  const sections = useMemo(() => {
    // No categories: every tag in one list, under no heading of its own.
    if (!def.hasCategories) {
      return [
        {
          slug: def.defaultGroup,
          label: 'Tags',
          items: [...visible].sort((a, b) => a.name.localeCompare(b.name)),
        },
      ]
    }
    const listed = new Set(groups.map((group) => group.slug))
    const orphans = [...new Set(visible.map((tag) => tag.group))]
      .filter((slug) => !listed.has(slug))
      .sort()
    return [...groups.map((group) => group.slug), ...orphans].map((slug) => ({
      slug,
      label: labelOf(slug),
      items: visible
        .filter((tag) => tag.group === slug)
        .sort((a, b) => a.name.localeCompare(b.name)),
    }))
  }, [visible, groups, labelOf, def])

  const total = tags?.length ?? 0

  return (
    <div className="tags">
      <header className="tags__head">
        <div>
          <h1 className="tags__title">Tag Manager</h1>
          <p className="tags__sub">
            {tags === null
              ? 'Loading…'
              : def.hasCategories
                ? `${total} tag${total === 1 ? '' : 's'} in ${groups.filter((g) => !g.hidden).length} categories in ${def.label}. Changes here apply to every image carrying the tag.`
                : `${total} tag${total === 1 ? '' : 's'} in ${def.label}, which has no categories. Changes here apply to every image carrying the tag.`}
          </p>
          <div className="tags__libraries" role="group" aria-label="Which library">
            {LIBRARIES.map((one) => (
              <button
                key={one.key}
                type="button"
                className={`tags__library${one.key === library ? ' tags__library--on' : ''}`}
                aria-pressed={one.key === library}
                onClick={() => setLibrary(one.key)}
              >
                {one.label}
              </button>
            ))}
          </div>
        </div>

        <input
          className="tags__search"
          type="search"
          value={filter}
          placeholder="Find a tag"
          spellCheck={false}
          onChange={(event) => setFilter(event.target.value)}
        />
      </header>

      {/* New tag. A tag with nothing on it is not a mistake — filing pictures
          under a category that does not exist yet is the harder order. */}
      <form
        className="tags__new"
        onSubmit={(event) => {
          event.preventDefault()
          const name = newName.trim()
          if (!name || busyId) return
          void run('new', async () => {
            await createTag(name, newGroup, library)
            setNewName('')
            return def.hasCategories
              ? `Created ${name} in ${labelOf(newGroup)}.`
              : `Created ${name}.`
          })
        }}
      >
        <input
          className="tags__input"
          value={newName}
          placeholder="New tag name"
          spellCheck={false}
          onChange={(event) => setNewName(event.target.value)}
        />
        {def.hasCategories ? (
        <select
            className="tags__select"
            value={newGroup}
            onChange={(event) => setNewGroup(event.target.value)}
          >
            {groups.map((group) => (
              <option key={group.slug} value={group.slug}>
                {group.label}
              </option>
            ))}
          </select>
        ) : null}
        <button
          type="submit"
          className="ww-btn ww-btn--secondary"
          disabled={!newName.trim() || busyId === 'new'}
        >
          Add
        </button>
      </form>

      <p className="tags__message" role="status" aria-live="polite">
        {error ? <span className="tags__message--bad">{error}</span> : (note ?? '')}
      </p>

      {/* -------------------------------------------------------------
          Categories
          -------------------------------------------------------------
          Folded away by default. Editing the categories is a rarer and
          heavier act than editing a tag — it rearranges the filter for
          everybody — so it should take one deliberate click to reach rather
          than sitting open above the work. */}
      {def.hasCategories ? (
      <section className="tags__categories">
        <button
          type="button"
          className="tags__categories-toggle"
          aria-expanded={showCategories}
          onClick={() => setShowCategories((open) => !open)}
        >
          <span className={`tags__caret${showCategories ? ' tags__caret--open' : ''}`} aria-hidden="true">
            &rsaquo;
          </span>
          Categories
          <span className="tags__group-count">{groups.length}</span>
        </button>

        {showCategories ? (
          <div className="tags__categories-body">
            <p className="tags__group-note">
              These are the rows of the library filter, in this order. A category can only be
              deleted once nothing is filed under it.
            </p>

            <ul className="tags__list">
              {groups.map((group, index) => {
                const count = tagsPerGroup.get(group.slug) ?? 0
                const busy = busyId === group.slug
                const above = groups[index - 1]
                const below = groups[index + 1]

                return (
                  <li className={`tags__row${busy ? ' tags__row--busy' : ''}`} key={group.slug}>
                    <div className="tags__row-main">
                      {editingGroup === group.slug ? (
                        <input
                          className="tags__input tags__input--inline"
                          value={draftGroupLabel}
                          autoFocus
                          spellCheck={false}
                          onChange={(event) => setDraftGroupLabel(event.target.value)}
                          onBlur={() => commitGroupRename(group)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') commitGroupRename(group)
                            if (event.key === 'Escape') setEditingGroup(null)
                          }}
                        />
                      ) : (
                        <button
                          type="button"
                          className="tags__name"
                          title="Rename this category"
                          onClick={() => {
                            setEditingGroup(group.slug)
                            setDraftGroupLabel(group.label)
                          }}
                        >
                          {group.label}
                          {group.hidden ? <span className="tags__pill">hidden</span> : null}
                        </button>
                      )}

                      <span className="tags__count">
                        {count} tag{count === 1 ? '' : 's'}
                      </span>

                      <div className="tags__actions">
                        <button
                          type="button"
                          className="ww-btn ww-btn--ghost ww-btn--sm"
                          disabled={busy || !above}
                          title="Move up"
                          onClick={() =>
                            above &&
                            void run(group.slug, async () => {
                              await swapTagGroupPositions(group, above)
                              return `${group.label} moved above ${above.label}.`
                            })
                          }
                        >
                          &uarr;
                        </button>
                        <button
                          type="button"
                          className="ww-btn ww-btn--ghost ww-btn--sm"
                          disabled={busy || !below}
                          title="Move down"
                          onClick={() =>
                            below &&
                            void run(group.slug, async () => {
                              await swapTagGroupPositions(group, below)
                              return `${group.label} moved below ${below.label}.`
                            })
                          }
                        >
                          &darr;
                        </button>
                        <button
                          type="button"
                          className="ww-btn ww-btn--ghost ww-btn--sm"
                          disabled={busy || count > 0}
                          title={
                            count > 0
                              ? 'Move its tags elsewhere first'
                              : 'Delete this category'
                          }
                          onClick={() =>
                            void run(group.slug, async () => {
                              await deleteTagGroup(group.slug)
                              return `${group.label} deleted.`
                            })
                          }
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </li>
                )
              })}
            </ul>

            <form
              className="tags__new tags__new--category"
              onSubmit={(event) => {
                event.preventDefault()
                const label = newCategory.trim()
                if (!label || busyId) return
                void run('new-category', async () => {
                  const created = await createTagGroup(label, groups, library, def.groupPrefix)
                  setNewCategory('')
                  return `Created the ${created.label} category. It is the last row of the filter — move it up if it belongs higher.`
                })
              }}
            >
              <input
                className="tags__input"
                value={newCategory}
                placeholder="New category name"
                spellCheck={false}
                onChange={(event) => setNewCategory(event.target.value)}
              />
              <button
                type="submit"
                className="ww-btn ww-btn--secondary"
                disabled={!newCategory.trim() || busyId === 'new-category'}
              >
                Add category
              </button>
            </form>
          </div>
        ) : null}
      </section>
      ) : null}

      {sections.map(({ slug, label, items }) => (
        <section className="tags__group" key={slug}>
          <h2 className="tags__group-name">
            {label}
            <span className="tags__group-count">{items.length}</span>
          </h2>

          {slug === 'system' ? (
            <p className="tags__group-note">
              These drive something else rather than appearing as filter chips. Moving one out of
              System makes it show up in the library filter.
            </p>
          ) : null}

          {items.length === 0 ? (
            <p className="tags__empty">Nothing filed here.</p>
          ) : (
            <ul className="tags__list">
              {items.map((tag) => {
                const count = tag.assetCount ?? 0
                const busy = busyId === tag.id
                const others = (tags ?? []).filter((other) => other.id !== tag.id)
                const target = others.find((other) => other.id === mergeTargetId) ?? null

                return (
                  <li className={`tags__row${busy ? ' tags__row--busy' : ''}`} key={tag.id}>
                    <div className="tags__row-main">
                      {editingId === tag.id ? (
                        <input
                          className="tags__input tags__input--inline"
                          value={draftName}
                          autoFocus
                          spellCheck={false}
                          onChange={(event) => setDraftName(event.target.value)}
                          onBlur={() => commitRename(tag)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') commitRename(tag)
                            if (event.key === 'Escape') setEditingId(null)
                          }}
                        />
                      ) : (
                        <button
                          type="button"
                          className="tags__name"
                          onClick={() => startRename(tag)}
                          title="Rename"
                        >
                          {tag.name}
                        </button>
                      )}

                      <span className="tags__count">{countLabel(count)}</span>

{def.hasCategories ? (
                                            <select
                        className="tags__select"
                        value={tag.group}
                        disabled={busy}
                        onChange={(event) => {
                          const next = event.target.value
                          void run(tag.id, async () => {
                            await setTagGroup(tag.id, next)
                            return `${tag.name} moved to ${labelOf(next)}.`
                          })
                        }}
                      >
                        {[
                          ...groups.map((group) => group.slug),
                          ...(groups.some((group) => group.slug === tag.group) ? [] : [tag.group]),
                        ].map((option) => (
                          <option key={option} value={option}>
                            {labelOf(option)}
                          </option>
                        ))}
                      </select>
                      ) : null}

                      <div className="tags__actions">
                        <button
                          type="button"
                          className="ww-btn ww-btn--ghost ww-btn--sm"
                          disabled={busy || others.length === 0}
                          onClick={() => (mergingId === tag.id ? setMergingId(null) : startMerge(tag))}
                        >
                          Merge
                        </button>
                        <button
                          type="button"
                          className="ww-btn ww-btn--ghost ww-btn--sm"
                          disabled={busy || count > 0}
                          title={
                            count > 0
                              ? 'Only a tag nothing carries can be deleted. Merge it instead.'
                              : 'Delete this tag'
                          }
                          onClick={() =>
                            void run(tag.id, async () => {
                              await deleteTag(tag.id)
                              return `${tag.name} deleted.`
                            })
                          }
                        >
                          Delete
                        </button>
                      </div>
                    </div>

                    {mergingId === tag.id ? (
                      <div className="tags__merge">
                        <span className="tags__merge-lead">Merge {tag.name} into</span>
                        <select
                          className="tags__select"
                          value={mergeTargetId}
                          onChange={(event) => setMergeTargetId(event.target.value)}
                        >
                          <option value="">Choose a tag&hellip;</option>
                          {others
                            .slice()
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((other) => (
                              <option key={other.id} value={other.id}>
                                {def.hasCategories ? `${other.name} — ${labelOf(other.group)}` : other.name}
                              </option>
                            ))}
                        </select>

                        {/* Spelled out rather than "are you sure": the number is
                            the part worth reading, and it is the part that makes
                            the wrong pair obvious. */}
                        <span className="tags__merge-warn">
                          {target
                            ? `${countLabel(count)} move to ${target.name}, and ${tag.name} is deleted. This cannot be undone.`
                            : ''}
                        </span>

                        <button
                          type="button"
                          className="ww-btn ww-btn--primary ww-btn--sm"
                          disabled={!target || busy}
                          onClick={() => target && commitMerge(tag, target)}
                        >
                          Merge
                        </button>
                        <button
                          type="button"
                          className="ww-btn ww-btn--ghost ww-btn--sm"
                          onClick={() => setMergingId(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : null}
                  </li>
                )
              })}
            </ul>
          )}
        </section>
      ))}
    </div>
  )
}
